import { useEffect, useState } from 'react'
import { UserPromptModal, type PromptVariable } from '@twasta/page-runtime/userMemory'
import {
  bootstrapUserMemory, saveUserMemory, notifySecurityContextChanged,
  type MemorySurface, type PromptValueIn,
} from '@/api/userMemory'

interface Props {
  /** A stable key for the session guard (e.g. the project folder). */
  projectId: string
  surface?: MemorySurface
  /** Override the bootstrap loader (e.g. the site-auth endpoints). */
  loader?: () => Promise<PromptVariable[]>
  /** Override the save handler (e.g. the site-auth endpoints). */
  saver?: (values: PromptValueIn[]) => Promise<void>
}

/**
 * Drop near the top of a running-app surface. On the first mount of a browser
 * session it asks the backend which `kind == "prompt"` project variables still
 * need a value (clearing stale non-remembered ones), and shows the shared
 * prompt modal. Renders nothing once satisfied. The session guard prevents a
 * mid-session refresh from re-clearing a session value.
 *
 * By default it talks to the platform `/api/user-memory` endpoints; pass
 * `loader`/`saver` to point it at another surface's auth (e.g. site preview).
 */
export default function UserMemoryGate({ projectId, surface = 'internal', loader, saver }: Props) {
  const [pending, setPending] = useState<PromptVariable[]>([])

  useEffect(() => {
    if (!projectId) return
    const guard = `twasta_um_boot_${projectId}_${surface}`
    let cancelled = false
    try {
      if (sessionStorage.getItem(guard)) return
    } catch { /* sessionStorage may be unavailable */ }
    const fetchPending = loader || (() => bootstrapUserMemory(projectId, surface))
    fetchPending()
      .then((list) => {
        if (cancelled) return
        setPending(list)
        // Nothing to ask → mark the session bootstrapped immediately.
        if (!list.length) {
          try { sessionStorage.setItem(guard, '1') } catch { /* ignore */ }
        }
      })
      .catch(() => { /* never block app load on this */ })
    return () => { cancelled = true }
  }, [projectId, surface, loader])

  if (!pending.length) return null

  const onSubmit = async (values: PromptValueIn[]) => {
    // A refused value (not on the list, or an entity this user holds no role
    // in) throws: the modal stays open and shows why, and nothing is marked
    // done for this session.
    await (saver ? saver(values) : saveUserMemory(projectId, values))
    try { sessionStorage.setItem(`twasta_um_boot_${projectId}_${surface}`, '1') } catch { /* ignore */ }
    setPending([])
    // Picking the SECURITY CONTEXT (e.g. the financial entity) changes which
    // roles apply — the menu, the buttons and every screen were loaded with
    // the rights from before the pick, so start the app again under the new one.
    if (pending.some((v) => v.security_context)) notifySecurityContextChanged()
  }

  return <UserPromptModal variables={pending} onSubmit={onSubmit} />
}
